import React, { useEffect, useRef, useMemo, useState } from 'react';
import {
  Box, Typography, Button, IconButton, Chip, Snackbar, Alert, CircularProgress,
  Paper, Stack, TextField, InputAdornment, Avatar, Tooltip, MenuItem,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  useMediaQuery, useTheme,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import EventNoteIcon from '@mui/icons-material/EventNote';
import CallIcon from '@mui/icons-material/Call';
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom';
import ScheduleIcon from '@mui/icons-material/Schedule';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useNavigate } from 'react-router-dom';
import { followUpApi } from '../api/apiClient';
import ConfirmDialog from '../common/ConfirmDialog';
import PageShell from '../common/PageShell';
import StatCard from '../common/StatCard';
import EmptyState from '../common/EmptyState';
import { format } from 'date-fns';

const info = JSON.parse(localStorage.getItem('device')) || null;

const modeMeta = (mode = '') => {
  const m = mode.toUpperCase();
  if (m === 'VISITING') return { color: '#2563eb', bg: '#dbeafe', icon: <MeetingRoomIcon fontSize="small" /> };
  if (m === 'CALLING') return { color: '#7c3aed', bg: '#ede9fe', icon: <CallIcon fontSize="small" /> };
  return { color: '#64748b', bg: '#f1f5f9', icon: <EventNoteIcon fontSize="small" /> };
};

const FollowUpList = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [search, setSearch] = useState('');
  const [modeFilter, setModeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const navigate = useNavigate();
  const fetchedRef = useRef(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await followUpApi.getAll();
      setRows(res.data || []);
    } catch {
      setSnackbar({ open: true, message: 'Failed to load data', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!info) {
      setSnackbar({
        open: true,
        message:
          'This app requires access to your location. Please enable location permission to proceed.',
        severity: 'warning',
      });
      return;
    }
    if (fetchedRef.current) return;
    fetchedRef.current = true;
    fetchData();
  }, []);

  const handleDelete = async () => {
    try {
      await followUpApi.delete(deleteId);
      setSnackbar({ open: true, message: 'Deleted successfully', severity: 'success' });
      fetchData();
    } catch {
      setSnackbar({ open: true, message: 'Delete failed', severity: 'error' });
    } finally {
      setDeleteId(null);
    }
  };

  const stats = useMemo(() => {
    const now = Date.now();
    return {
      total: rows.length,
      visiting: rows.filter((r) => r.mode === 'VISITING').length,
      calling: rows.filter((r) => r.mode === 'CALLING').length,
      upcoming: rows.filter(
        (r) => r.followUpDate && new Date(r.followUpDate).getTime() > now
      ).length,
    };
  }, [rows]);

  const filtered = useMemo(() => {
    return rows.filter((r) => {
      const matchesSearch =
        !search ||
        [r.companyName, r.description, r.mode, r.status]
          .filter(Boolean)
          .some((v) => String(v).toLowerCase().includes(search.toLowerCase()));
      const matchesMode = modeFilter === 'all' || r.mode === modeFilter;
      const matchesStatus = statusFilter === 'all' || r.status === statusFilter;
      return matchesSearch && matchesMode && matchesStatus;
    });
  }, [rows, search, modeFilter, statusFilter]);

  return (
    <PageShell
      icon={<EventNoteIcon />}
      title="Follow-Ups"
      subtitle="Track calls, visits and engagement with your companies"
      breadcrumbs={[{ label: 'Dashboard', to: '/' }, { label: 'Follow-Ups' }]}
      actions={
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/follow-ups/new')}
          sx={{
            bgcolor: '#fff',
            color: '#1e3a8a',
            fontWeight: 700,
            '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' },
          }}
        >
          Add Follow-Up
        </Button>
      }
    >
      {/* Stats */}
      <Box
        sx={{
          display: 'grid',
          gap: 2,
          mb: 3,
          gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(4, 1fr)' },
        }}
      >
        <StatCard label="Total Follow-Ups" value={stats.total} icon={<EventNoteIcon />} color="#2563eb" />
        <StatCard label="Visiting" value={stats.visiting} icon={<MeetingRoomIcon />} color="#0891b2" />
        <StatCard label="Calling" value={stats.calling} icon={<CallIcon />} color="#7c3aed" />
        <StatCard label="Upcoming" value={stats.upcoming} icon={<ScheduleIcon />} color="#ea580c" />
      </Box>

      {/* Filter bar */}
      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 2,
          borderRadius: 2.5,
          border: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 1.5,
          alignItems: { xs: 'stretch', sm: 'center' },
        }}
      >
        <TextField
          size="small"
          placeholder="Search company, description, mode..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ flex: 1 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        <TextField
          select
          size="small"
          value={modeFilter}
          onChange={(e) => setModeFilter(e.target.value)}
          sx={{ minWidth: 140 }}
        >
          <MenuItem value="all">All Modes</MenuItem>
          <MenuItem value="VISITING">Visiting</MenuItem>
          <MenuItem value="CALLING">Calling</MenuItem>
        </TextField>
        <TextField
          select
          size="small"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          sx={{ minWidth: 140 }}
        >
          <MenuItem value="all">All Status</MenuItem>
          <MenuItem value="PENDING">Pending</MenuItem>
          <MenuItem value="COMPLETED">Completed</MenuItem>
        </TextField>
        <Tooltip title="Refresh">
          <IconButton onClick={fetchData} disabled={loading}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Paper>

      {/* Table */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
          <CircularProgress />
        </Box>
      ) : filtered.length === 0 ? (
        <Paper elevation={0} sx={{ borderRadius: 2.5, border: '1px solid', borderColor: 'divider' }}>
          <EmptyState
            icon={<EventNoteIcon />}
            title={rows.length === 0 ? 'No follow-ups yet' : 'No matches'}
            subtitle={
              rows.length === 0
                ? 'Log your first call or visit to start tracking engagement.'
                : 'Try adjusting your search or filters.'
            }
            actionLabel={rows.length === 0 ? 'Add Follow-Up' : undefined}
            onAction={() => navigate('/follow-ups/new')}
          />
        </Paper>
      ) : (
        <Paper
          elevation={0}
          sx={{ borderRadius: 2.5, border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}
        >
          <TableContainer>
            <Table sx={{ minWidth: isMobile ? 0 : 900 }}>
              <TableHead>
                <TableRow
                  sx={{
                    '& th': {
                      bgcolor: 'grey.50',
                      fontWeight: 700,
                      fontSize: 12,
                      textTransform: 'uppercase',
                      letterSpacing: 0.5,
                      color: 'text.secondary',
                      borderBottom: '2px solid',
                      borderColor: 'divider',
                    },
                  }}
                >
                  <TableCell>Company</TableCell>
                  <TableCell>Mode</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Follow-Up Date</TableCell>
                  {!isMobile && <TableCell>Description</TableCell>}
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.map((row) => {
                  const meta = modeMeta(row.mode);
                  return (
                    <TableRow
                      key={row.id}
                      hover
                      sx={{ cursor: 'pointer', '&:last-child td': { borderBottom: 0 } }}
                      onClick={() => navigate(`/follow-ups/view/${row.id}`)}
                    >
                      <TableCell>
                        <Stack direction="row" spacing={1.5} alignItems="center">
                          <Avatar
                            sx={{
                              bgcolor: meta.bg,
                              color: meta.color,
                              width: 38,
                              height: 38,
                            }}
                          >
                            {meta.icon}
                          </Avatar>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography variant="body2" fontWeight={700} noWrap>
                              {row.companyName || '—'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              #{row.id}
                            </Typography>
                          </Box>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={row.mode}
                          size="small"
                          sx={{
                            bgcolor: meta.bg,
                            color: meta.color,
                            fontWeight: 700,
                            border: 'none',
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        {row.status ? (
                          <Chip
                            label={row.status}
                            size="small"
                            color={row.status === 'PENDING' ? 'warning' : 'success'}
                            variant="outlined"
                            sx={{ fontWeight: 600 }}
                          />
                        ) : (
                          <Typography variant="caption" color="text.disabled">
                            —
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {row.followUpDate
                            ? format(new Date(row.followUpDate), 'PP p')
                            : '—'}
                        </Typography>
                      </TableCell>
                      {!isMobile && (
                        <TableCell>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              maxWidth: 280,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {row.description || '—'}
                          </Typography>
                        </TableCell>
                      )}
                      <TableCell align="right">
                        <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                          <Tooltip title="View">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/follow-ups/view/${row.id}`);
                              }}
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Edit">
                            <IconButton
                              size="small"
                              color="info"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/follow-ups/edit/${row.id}`);
                              }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={(e) => {
                                e.stopPropagation();
                                setDeleteId(row.id);
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      <ConfirmDialog
        open={!!deleteId}
        title="Delete Follow-Up"
        message="Are you sure you want to delete this follow-up? This action cannot be undone."
        onConfirm={handleDelete}
        onCancel={() => setDeleteId(null)}
      />
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>
    </PageShell>
  );
};

export default FollowUpList;